import type { WizardWithConditional } from '@lemonade-hq/chat-wizard-common-definitions';
import type { Milliseconds } from '@lemonade-hq/ts-helpers';
import type { IconName } from '@lemonade-hq/uniclient-theme';
import type { UniclientAction } from './action';
import type { UniclientImage } from './components/image';
import type { InterpolableTextProps } from './components/interpolable_text';
import { specialChars } from './specialChars';
import type { UniclientSummary } from './summary';
import type { FormattedText } from './utils';

export type UniclientAvatar = 'jim' | 'maya';

export interface BaseUniclientProgressItem {
  readonly title: string;
  readonly id: string;
}

export interface LevelThreeProgressItem extends BaseUniclientProgressItem {}

export interface LevelTwoProgressItem extends BaseUniclientProgressItem {
  readonly subItems?: LevelThreeProgressItem[];
}

export interface LevelOneProgressItem extends BaseUniclientProgressItem {
  readonly status?: 'completed' | 'notice' | 'pending';
  readonly disabled?: boolean;
  readonly action?: UniclientAction;
  readonly subItems?: LevelTwoProgressItem[];
}

// Adding this rename only in order not the introduce a breaking change
export type UniclientProgressItem = LevelOneProgressItem;

export interface UniclientProgress {
  readonly currentItemId: string;
  readonly items: UniclientProgressItem[];
}

export type UniclientCoInsurance =
  | {
      readonly logo: string;
      readonly alt: string;
      readonly position: 'bottom';
      readonly mobileLogo?: string;
      readonly text?: string;
      readonly links?: InterpolableTextProps[];
      readonly expanded?: boolean;
    }
  | {
      readonly logo: string;
      readonly alt: string;
      readonly position: 'top';
      readonly mobileLogo?: string;
      readonly delimiter?: '|' | 'x';
      readonly text?: never;
      readonly links?: never;
      readonly expanded?: never;
    };

export type UniclientHeaderTitle = string | { readonly text: string; readonly icon: IconName };

export type UniclientHeaderMobileCenter = 'logo' | 'progress' | 'title';

export interface ToastAction {
  readonly content: string;
  readonly type: 'dismiss' | 'refresh';
}

export interface UniclientToast {
  readonly icon?: IconName;
  readonly title: string;
  readonly action?: ToastAction | UniclientAction;
  readonly type?: 'attention' | 'success' | 'system';
  readonly timeout?: number;
  readonly id?: string;
}

export type UniclientTitleMode = 'reasoning' | 'typing';

export type UniclientComponentTitle =
  | string
  | {
      readonly text: FormattedText;
      readonly mode: 'typing';
    }
  | {
      readonly text: string;
      readonly mode: 'reasoning';
    };

export interface UniclientBaseFrame<TType extends string = string> {
  readonly type?: TType;
  /** @optional question title / the question itself */
  readonly title?: UniclientComponentTitle;

  /** @optional progress bar of the flow */
  readonly progress?: UniclientProgress;

  /** partner logo svg url if needed */
  readonly coInsurance?: UniclientCoInsurance;

  /** @optional The step summary card */
  readonly summary?: UniclientSummary;

  /** lock the history of the chat so the user can't scroll up and see previous messages */
  readonly lockHistory?: boolean;

  readonly header?: {
    /** actions displayed in the menu */
    readonly actions?: UniclientHeaderActions;
    /** @optional title to appear on mobile header (will hide progress if exists) */
    readonly title?: UniclientHeaderTitle;

    readonly mobile?: {
      /** @optional center logo, progress, or title on mobile */
      readonly center?: UniclientHeaderMobileCenter;
    };

    /** @optional hide the logo on the header */
    readonly hideLogo?: boolean;
  };
  /** @optional toast(s) to show to the user available types: 'attention' | 'success' | 'system'. Single toast can have optional id, array requires ids */
  readonly toast?: (UniclientToast & { readonly id: string })[] | UniclientToast;

  readonly disclaimer?: InterpolableTextProps;

  /** @optional open the free text input or show the button to toggle the input, if not passed the feature is disabled */
  readonly freeTextInput?: 'open' | 'toggle';
}

export interface UniclientSurveyProps {
  readonly scoreAttribute: string;
  readonly commentAttribute: string;
}

export interface UniclientMessageFrame<TComponentAttributes extends string = string>
  extends UniclientBaseFrame<'message'> {
  /** the avatar shown at the chat header. can be omitted in order to not show anything with 'hidden', if nothing is passed maya will be used */
  readonly avatar?: UniclientAvatar | 'hidden';

  /** overriding optional, making title mandatory in message type frames */
  readonly title: UniclientComponentTitle;

  /** the main action of the frame */
  readonly action?: UniclientAction<TComponentAttributes>;

  /** @optional secondary action for the frame */
  readonly secondaryAction?: UniclientAction<TComponentAttributes>;

  /** enable the user to edit the answer */
  readonly editable?: boolean;

  /** explanation bubble */
  readonly explanation?: InterpolableTextProps;

  /** background images for mobile and desktop */
  readonly backgroundImage?: {
    readonly sm: string;
    readonly lg: string;
  };

  /** show a survey to the user to ask for feedback */
  readonly survey?: UniclientSurveyProps;
}

export interface UniclientPageFrame extends UniclientBaseFrame<'page'> {
  /**
   * An action that can be triggered automatically but will not be rendered on the screen.
   * This should only be used for auto-triggers and not for user-interactive elements.
   */
  readonly action?: UniclientAction;
  // reset MessageFrame props for compatibility
  readonly secondaryAction?: never;
  readonly explanation?: never;
  readonly backgroundImage?: never;
  readonly eventPrefix?: 'policy' | 'quote';
}

export interface UniclientLandingPageFrame extends UniclientBaseFrame<'landingPage'> {
  readonly logo: UniclientImage;
  readonly action: UniclientAction;
  readonly secondaryAction?: never;
}

export type UniclientFrame<TComponentAttributes extends string = string> =
  | UniclientLandingPageFrame
  | UniclientMessageFrame<TComponentAttributes>
  | UniclientPageFrame;

export type UniclientFrameType = UniclientFrame['type'];

export function isUniclientFrameOfType<TType extends UniclientFrameType>(
  frame: UniclientFrame | undefined,
  type: TType,
): frame is Extract<UniclientFrame, { readonly type?: TType }> {
  return frame != null && frame.type === type;
}

export type UniclientConditionalFrame = UniclientFrame & WizardWithConditional;

export type HeaderAction = UniclientAction & {
  readonly disabledOnDesktop?: boolean;
  readonly nativeMobileOnly?: boolean;
  readonly icon: IconName;
  readonly onlyWhenLoggedIn?: boolean;
};

export enum PredefinedMenuOptionType {
  Edit = 'Edit',
  StartOver = 'StartOver',
  Logout = 'Logout',
  AskUsAnything = 'AskUsAnything',
  Exit = 'Exit',
  ImmediateExit = 'ImmediateExit',
  FreeTextInput = 'FreeTextInput',
}

type MenuSubActionSection = {
  readonly title: string;
  readonly actions: (HeaderAction | PredefinedMenuOptionType)[];
};

export type MenuSubActions = (HeaderAction | MenuSubActionSection | PredefinedMenuOptionType)[];

export interface HeaderMenuProps {
  readonly icon: IconName;
  readonly subActions: MenuSubActions;
  readonly showUserName?: boolean;
}

export type HeaderActionType = HeaderAction | HeaderMenuProps;

export type UniclientHeaderActions = {
  readonly right?: HeaderActionType[];
  readonly left?: HeaderAction | PredefinedMenuOptionType.Exit | PredefinedMenuOptionType.ImmediateExit | null;
};

export const defaultHeaderActions = {
  right: [
    {
      icon: 'three-dots',
      subActions: [PredefinedMenuOptionType.Edit, PredefinedMenuOptionType.StartOver, PredefinedMenuOptionType.Logout],
      showUserName: false,
    },
  ],
  left: PredefinedMenuOptionType.Exit,
} as const satisfies UniclientHeaderActions;

export const BASE_FRAME_TRANSITION_DURATION: Milliseconds = 300;

export const FRAME = {
  specialChars,
} as const;
